import React from "react";
import { Link as RouterLink } from "react-router";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import DateChip from "components/DateChip";
import { LightTooltip } from "components/Tooltip";
import { TableHeadCell } from "components/Table/types";

import { IbanInvoice } from "store/features/ibanInvoiceSlice";
import { IbanAccount } from "store/features/ibanAccountSlice";
import { BankToken } from "store/features/bankIntegrationsSlice";

import { AmountTypeEnum } from "api";
import { routes } from "routes/routes";

import { ReactComponent as StarIcon } from "assets/images/icon-star.svg";
import { ReactComponent as StarBorderIcon } from "assets/images/icon-star-border.svg";

import { getPriceFormat } from "shared/functions";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

import dayjs from "dayjs";

const onDetailsClick = () => {
  analytics().sendConversionEvent(
    AnalyticsEvents.invoices_view_invoice_details,
    null,
    true,
  );
};

// TODO: remove after feature release
const invoiceDetailsColumn: TableHeadCell<IbanInvoice> = {
  label: "",
  id: "id",
  width: 60,
  cell: (id) => {
    const isXs = typeof window !== "undefined" && window.innerWidth <= 1040;
    return (
      <Box sx={{ mt: isXs ? 1.5 : 0 }}>
        <Link
          component={RouterLink}
          variant="body2"
          fontWeight="700"
          color="blue600"
          underline="none"
          to={`${routes.invoices}/${id}`}
          onClick={onDetailsClick}
        >
          Деталі
        </Link>
      </Box>
    );
  },
};

export const getInvoiceColumns = (
  onToggleFavorite: (invoice: IbanInvoice) => void,
  showDetails = false,
): TableHeadCell<IbanInvoice>[] => [
  {
    label: "",
    id: "isFavorite",
    width: 50,
    cell: (favorite, cell) => (
      <IconButton
        aria-label="toggle-favorite"
        sx={{ p: "5px" }}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(cell);
        }}
      >
        {favorite ? <StarIcon /> : <StarBorderIcon />}
      </IconButton>
    ),
  },
  {
    label: "Реквізити",
    id: "iban",
    cell: (_, cell) => (
      <>
        <Typography noWrap variant="body2" sx={{ mb: 1 }}>
          {cell.organizationName || ""}
        </Typography>
        <Typography noWrap variant="body2" color="grey700" sx={{ mb: 1 }}>
          {cell.iban || ""}
        </Typography>
      </>
    ),
    width: "20%",
  },
  {
    label: "Призначення",
    id: "paymentPurpose",
    width: "30%",
    cell: (_, cell) =>
      (() => {
        const showTooltip =
          typeof window !== "undefined" &&
          window.innerWidth > 900 &&
          window.innerWidth < 1300 &&
          cell.paymentPurpose?.toString().length > 100;

        return (
          <LightTooltip
            arrow
            title={cell.paymentPurpose || ""}
            sx={{ display: showTooltip ? "block" : "none" }}
          >
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                display: "-webkit-box",
                overflow: "hidden",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 5,
                overflowWrap: "anywhere",
              }}
            >
              {cell.paymentPurpose || ""}
            </Typography>
          </LightTooltip>
        );
      })(),
  },
  {
    label: "Дата",
    id: "invoiceDateTimeUtc",
    color: "grey700",
    transform: (value) =>
      dayjs
        .utc(String(value ?? ""))
        .local()
        .format("DD.MM.YYYY HH:mm"),
    width: 140,
  },
  {
    label: "Термін дії",
    id: "expirationDateTimeUtc",

    cell: (_, cell) =>
      (() => {
        const { expirationDateTimeUtc, invoiceDateTimeUtc } = cell;

        const formattedCreated = dayjs
          .utc(invoiceDateTimeUtc)
          .local()
          .format("DD.MM.YYYY HH:mm");

        const formattedExpiration = dayjs
          .utc(expirationDateTimeUtc)
          .local()
          .format("DD.MM.YYYY HH:mm");

        const isXs = typeof window !== "undefined" && window.innerWidth <= 1040;

        if (isXs) {
          return (
            <Box sx={{ mb: 1.5 }}>
              <DateChip
                date={
                  expirationDateTimeUtc
                    ? `діє до ${formattedExpiration}`
                    : formattedCreated
                }
              />
            </Box>
          );
        }

        return (
          <Typography variant="body2" color="grey700">
            {expirationDateTimeUtc ? formattedExpiration : ""}
          </Typography>
        );
      })(),
    width: 140,
  },
  {
    label: "Сума, грн",
    id: "amount",
    align: "right",
    transform: (value, cell) => {
      if (cell?.amountType === AmountTypeEnum.PayerDefined) {
        const isXs = typeof window !== "undefined" && window.innerWidth <= 1040;
        return isXs ? "Довільна сума" : "Довільна";
      }

      return `${getPriceFormat(Number(value || 0))} ₴`;
    },
    width: 125,
  },
  {
    label: "Нотатка",
    id: "notes",
    width: "10%",
    cell: (_, cell) =>
      (() => {
        const showTooltip =
          typeof window !== "undefined" &&
          ((window.innerWidth > 900 &&
            window.innerWidth < 1600 &&
            (cell?.notes || "").length > 30) ||
            (window.innerWidth >= 1600 && (cell?.notes || "").length > 50));

        return (
          <LightTooltip
            arrow
            title={cell?.notes || ""}
            sx={{ display: showTooltip ? "block" : "none" }}
          >
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                display: "-webkit-box",
                overflow: "hidden",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 5,
                overflowWrap: "anywhere",
              }}
            >
              {cell?.notes || ""}
            </Typography>
          </LightTooltip>
        );
      })(),
  },
  ...(showDetails ? [invoiceDetailsColumn] : []),
];

export const accountColumnsDesktop: TableHeadCell<IbanAccount>[] = [
  {
    label: "Назва",
    id: "organizationName",
    width: "25%",
    cell: (_, cell) => (
      <>
        <Typography sx={{ overflowWrap: "anywhere" }} variant="body2">
          {cell.organizationName || ""}
        </Typography>
        {cell?.publicName && (
          <Typography
            noWrap
            variant="subtitle2"
            color="grey700"
            sx={{ mt: 0.5 }}
          >
            {cell.publicName}
          </Typography>
        )}
      </>
    ),
  },
  {
    label: "ЄДРПОУ / РНОКПП",
    id: "identificationCode",
    width: 180,
    color: "grey700",
  },
  {
    label: "IBAN",
    id: "iban",
    width: "30%",
    cell: (_, cell) => (
      <>
        <Typography noWrap variant="body2">
          {cell.iban || ""}
        </Typography>
        {cell?.bank?.name && (
          <Typography
            noWrap
            variant="subtitle2"
            color="grey700"
            sx={{ mt: 0.5 }}
          >
            {cell.bank.name}
          </Typography>
        )}
      </>
    ),
  },
  {
    label: "Статус",
    id: "isSuspended",
    width: 160,
    align: "center",
    cell: (value) => {
      return (
        <Chip
          label={value ? "Призупинено" : "Активний"}
          color={value ? "error" : "success"}
        />
      );
    },
  },
];

export const accountColumnsMobile: TableHeadCell<IbanAccount>[] = [
  {
    label: "Статус",
    id: "isSuspended",
    width: 160,
    align: "center",
    cell: (value) => {
      return (
        <Typography
          variant="body2"
          color={value ? "red800" : "green800"}
          sx={{ mb: 1 }}
        >
          {value ? "Призупинено" : "Активний"}
        </Typography>
      );
    },
  },
  {
    label: "Публічна назва",
    id: "publicName",
    width: "25%",
  },
  {
    label: "Юридична назва",
    id: "organizationName",
    width: "25%",
  },
  {
    label: "ЄДРПОУ / РНОКПП",
    id: "identificationCode",
    width: 180,
    cell: (_, cell) => (
      <Typography noWrap variant="subtitle2" color="grey700" sx={{ mb: 1 }}>
        {cell?.identificationCode || ""}
      </Typography>
    ),
  },
  {
    label: "IBAN",
    id: "iban",
    width: "30%",
    cell: (_, cell) => (
      <Typography noWrap variant="subtitle2" color="grey700" sx={{ mb: 1 }}>
        {cell?.iban || ""}
      </Typography>
    ),
  },
  {
    label: "Банк",
    id: "bank",
    width: "30%",
    cell: (_, cell) => (
      <Typography noWrap variant="body2">
        {cell?.bank?.name || ""}
      </Typography>
    ),
  },
];

export const bankTokenColumns: TableHeadCell<BankToken>[] = [
  {
    label: "Банк",
    id: "bank",
    transform: (value) =>
      typeof value === "string" ? value : value?.name || "",
  },
  {
    label: "Назва",
    id: "name",
    secure: true,
  },
  {
    label: "Дата",
    id: "createdDateUtc",
    color: "grey700",
    transform: (value) =>
      dayjs
        .utc(String(value ?? ""))
        .local()
        .format("MMM DD, YYYY"),
  },
  // {
  //   label: "Статус",
  //   id: "identificationCode",
  //   width: 132,
  //   color: "grey700",
  // },
];
